<template>
  <div class="p-8">
    <h1 class="text-2xl font-bold mb-4">Manage Services</h1>

    <!-- Add/Edit Service Form -->
    <form @submit.prevent="saveService" class="mb-8 p-4 border rounded-lg">
      <h2 class="text-xl font-semibold mb-2">{{ isEditing ? 'Edit Service' : 'Add Service' }}</h2>
      <div class="grid grid-cols-2 gap-4">
        <input v-model="serviceForm.name" placeholder="Name" class="p-2 border rounded" />
        <input v-model="serviceForm.slug" placeholder="Slug (e.g., vidsabio, scenario_builder)" class="p-2 border rounded" />
        <input v-model="serviceForm.category" placeholder="Category" class="p-2 border rounded" />
        <select v-model="serviceForm.is_active" class="p-2 border rounded">
          <option :value="true">Active</option>
          <option :value="false">Inactive</option>
        </select>
        <textarea v-model="serviceForm.description" placeholder="Description" class="p-2 border rounded col-span-2"></textarea>
        <div class="text-sm text-gray-600 col-span-2">
          <strong>Subdomain:</strong> {{ getSubdomain(serviceForm.slug) }}
          <br><small class="text-gray-500">Spark costs will be configured in individual apps</small>
        </div>
      </div>
      <div class="mt-4">
        <button type="submit" class="bg-blue-500 text-white px-4 py-2 rounded">{{ isEditing ? 'Update' : 'Create' }}</button>
        <button v-if="isEditing" @click="cancelEdit" class="bg-gray-500 text-white px-4 py-2 rounded ml-2">Cancel</button>
      </div>
    </form>

    <!-- Service List -->
    <div class="overflow-x-auto">
      <table class="min-w-full bg-white">
        <thead class="bg-gray-800 text-white">
          <tr>
            <th class="py-2 px-4">Name</th>
            <th class="py-2 px-4">Slug</th>
            <th class="py-2 px-4">Subdomain</th>
            <th class="py-2 px-4">Description</th>
            <th class="py-2 px-4">Category</th>
            <th class="py-2 px-4">Status</th>
            <th class="py-2 px-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="service in services" :key="service.id">
            <td class="border px-4 py-2">{{ service.name }}</td>
            <td class="border px-4 py-2 font-mono text-sm">{{ service.slug }}</td>
            <td class="border px-4 py-2 text-blue-600">
              <a :href="getSubdomain(service.slug)" target="_blank" class="hover:underline">
                {{ getSubdomain(service.slug) }}
              </a>
            </td>
            <td class="border px-4 py-2">{{ service.description }}</td>
            <td class="border px-4 py-2">{{ service.category }}</td>
            <td class="border px-4 py-2">
              <span :class="getStatusClass(service.is_active)" class="px-2 py-1 rounded text-sm">
                {{ service.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="border px-4 py-2">
              <button @click="editService(service)" class="bg-yellow-500 text-white px-2 py-1 rounded">Edit</button>
              <button @click="deleteService(service.id)" class="bg-red-500 text-white px-2 py-1 rounded ml-2">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useAuthStore } from '../../stores/auth';

const authStore = useAuthStore();
const services = ref([]);
const isEditing = ref(false);
const serviceForm = ref({
  id: null,
  name: '',
  slug: '',
  description: '',
  category: '',
  is_active: true
});

const api = import.meta.env.VITE_API_URL;

async function fetchServices() {
  const response = await fetch(`${api}/admin/products`, {
    headers: {
      'Authorization': `Bearer ${authStore.accessToken}`
    }
  });
  services.value = await response.json();
}

async function saveService() {
  if (isEditing.value) {
    await fetch(`${api}/admin/products/${serviceForm.value.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authStore.accessToken}`
      },
      body: JSON.stringify(serviceForm.value)
    });
  } else {
    // Remove id from request body when creating to let database generate UUID
    // Set default spark cost values (will be configured in individual apps)
    const { id, spark_cost_value, ...productData } = serviceForm.value;
    const productPayload = {
      ...productData,
      spark_cost_type: 'flat',
      spark_cost_value: 0,
      spark_cost_formula: null
    };
    await fetch(`${api}/admin/products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authStore.accessToken}`
      },
      body: JSON.stringify(productPayload)
    });
  }
  cancelEdit();
  await fetchServices();
}

function editService(service) {
  isEditing.value = true;
  serviceForm.value = { ...service };
}

function cancelEdit() {
  isEditing.value = false;
  serviceForm.value = {
    id: null,
    name: '',
    slug: '',
    description: '',
    category: '',
    is_active: true
  };
}

// Function to generate subdomain URL from slug
function getSubdomain(slug) {
  if (!slug) return '#';
  
  // Special cases that don't follow the pattern
  const specialCases = {
    dashboard: "https://sparks.happyalien.ai",
    ai_media_app: "https://media.happyalien.ai",
  };
  
  // Check for special cases first
  if (specialCases[slug]) {
    return specialCases[slug];
  }
  
  // Convert slug to subdomain format
  // Examples: 
  // scenario_builder -> scenario
  // media_generator -> media
  // vidsabio -> vidsabio (no underscore, keep as is)
  // learner_analytics -> analytics
  
  // If slug contains underscore, take the last part for common patterns
  if (slug.includes('_')) {
    const parts = slug.split('_');
    // Special handling for known patterns
    if (parts[parts.length - 1] === 'builder' || parts[parts.length - 1] === 'generator') {
      return `https://${parts[0]}.happyalien.ai`;
    }
    if (parts[0] === 'learner' && parts[1] === 'analytics') {
      return `https://analytics.happyalien.ai`;
    }
  }
  
  // Default: use the slug as-is
  return `https://${slug.replace(/_/g, '-')}.happyalien.ai`;
}

// Function to get status styling
function getStatusClass(isActive) {
  return isActive 
    ? 'bg-green-100 text-green-800'
    : 'bg-red-100 text-red-800';
}

async function deleteService(id) {
  await fetch(`${api}/admin/products/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authStore.accessToken}`
    }
  });
  await fetchServices();
}

onMounted(fetchServices);
</script>